import { useState, useEffect } from "react";
import { geocodeByPlaceId, getLatLng } from "react-places-autocomplete";

// Components
import {
  Card,
  Header,
  Body,
  DataTable,
  TextField,
  Button,
  GooglePlacesAutoComplete,
  NumberFormatterInput
} from "../../../../components/UI";
import { Label, Heading2 } from "../../../../components/typography";
import AssignedManagerItem from "../../../../components/lists/manageBranches/AssignedManager.item";

// Builders
import * as buttonDesignType from "../../../../builders/buttonDesign.types";

// Form Models
import { BranchFormModel } from "../../../../models/app/forms";

// Calls
import { fetchManagersRequest } from "../../../../api/calls/manager.call";

// Styles
import { BranchFormStyles } from "./branchForm.styles";
import { isEmpty } from "../../../../utils";

const managersColumns = [
  { title: "Name", width: "1" },
  { title: "Contact Number", width: "190px" },
  { title: "Email", width: "240px" }
];

const BranchForm = ({
  values,
  errors,
  touched,
  dirty,
  setFieldValue,
  setFieldTouched,
  setValues,
  handleChange,
  handleBlur
}) => {
  const [managersOptions, setManagersOptions] = useState([]);

  const fetchManagers = async () => {
    const response = await fetchManagersRequest();
    if (response.hasError) {
    }
    setManagersOptions(response.managers);
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const chosenManagers = () => {
    return managersOptions.filter(option =>
      values.managers.includes(option.id)
    );
  };

  /**
   * Address change
   * @param address - string
   * @returns void
   */
  const handleAddressChange = address => {
    setFieldValue("address", address);
  };

  /**
   * Prefill location fields based on selected address from drop down
   * @param address - string
   * @returns void
   */
  const prefillFieldsBasedOnAddress = async (chosenAddress, placeId) => {
    const [place] = await geocodeByPlaceId(placeId);
    const latLng = await getLatLng(place);

    const { long_name: postalCode = "" } =
      place.address_components.find(c => c.types.includes("postal_code")) || {};
    const { long_name: state = "" } =
      place.address_components.find(c => c.types.includes("country")) || {};
    const { long_name: city = "" } =
      place.address_components.find(c => c.types.includes("locality")) || {};
    const address = place.formatted_address;
    const longitude = latLng.lng;
    const latitude = latLng.lat;

    setValues(
      new BranchFormModel({
        ...values,
        city,
        state,
        address,
        latitude,
        longitude,
        postalCode
      })
    );
  };

  return (
    <BranchFormStyles>
      <Card rounded>
        <Header>
          <div className="cardHeaderRow">
            <Heading2>BRANCH INFO</Heading2>
            {dirty && (
              <Button
                type="submit"
                className="sectionSaveBtn"
                buttonDesignType={buttonDesignType.PRIMARY}
              >
                Save Changes
              </Button>
            )}
          </div>
        </Header>
        <Body>
          <div className="fieldRow">
            <div className="fieldGroup">
              <Label>Branch Name</Label>
              <TextField
                className="input"
                name="name"
                value={values.name}
                touched={touched.name}
                error={errors.name}
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Enter Branch Name"
              />
            </div>
            <div className="fieldGroup" />
          </div>

          <div className="sectionDivider" />

          <div className="fieldRow">
            <div className="fieldGroup">
              <Label>Branch Address</Label>
              <GooglePlacesAutoComplete
                value={values.address}
                touched={touched.address}
                error={errors.address}
                onBlur={handleBlur}
                handleChange={handleAddressChange}
                handleSelect={prefillFieldsBasedOnAddress}
                name="address"
                placeholder="Enter Branch Address"
                className="input"
              />
            </div>
            <div className="fieldGroup" />
          </div>
          <div className="fieldRow threeCol">
            <div className="fieldGroup">
              <Label>Branch State</Label>
              <TextField
                className="input"
                name="state"
                value={values.state}
                touched={touched.state}
                error={errors.state}
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Enter Branch State"
              />
            </div>
            <div className="fieldGroup">
              <Label>Branch City</Label>
              <TextField
                className="input"
                name="city"
                value={values.city}
                touched={touched.city}
                error={errors.city}
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Enter Branch City"
              />
            </div>
            <div className="fieldGroup">
              <Label>Branch Postal/ZIP Code</Label>
              <TextField
                className="input"
                name="postalCode"
                value={values.postalCode}
                touched={touched.postalCode}
                error={errors.postalCode}
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Enter Branch Code"
              />
            </div>
          </div>

          <div className="sectionDivider" />

          <div className="fieldRow">
            <div className="fieldGroup">
              <Label>Branch Contact Number</Label>
              <NumberFormatterInput
                value={values.contactNumber}
                error={errors.contactNumber}
                touched={touched.contactNumber}
                onBlur={setFieldTouched}
                onChange={setFieldValue}
                className="input"
                name="contactNumber"
                placeholder="Enter a Branch Contact Number"
                format="###-###-####"
              />
            </div>
            <div className="fieldGroup" />
          </div>
        </Body>
      </Card>

      {!isEmpty(chosenManagers()) && (
        <Card rounded>
          <Header>
            <Heading2>ASSIGNED MANAGERS</Heading2>
          </Header>
          <DataTable
            columns={managersColumns}
            data={chosenManagers()}
            disableHover
            renderRow={manager => (
              <AssignedManagerItem
                fullName={manager.fullName}
                email={manager.email}
                contactNumber={manager.contactNumber}
              />
            )}
          />
        </Card>
      )}
    </BranchFormStyles>
  );
};

export default BranchForm;
